/**
 * Declarative transition table for the sign-up step machine.
 *
 * Every routing decision in SignUpProvider.js is expressed here as data. The
 * provider computes an `outcome` from whatever it just did (a validation call,
 * an auth call, a form submit) and asks this table where to go; it no longer
 * decides destinations inline.
 *
 * Shape of a row:
 *   { from, outcome, guard, to }
 *     from     - step the machine is in, or a BOOTSTRAP/global pseudo-state
 *     outcome  - what just happened, computed by the provider
 *     guard    - one name, or an array of names, resolved against guards.js.
 *                An array means EVERY guard must pass (AND); rows are
 *                evaluated IN ORDER and the first whose guards all pass wins,
 *                which is how an if / else-if / else chain is preserved as
 *                data. An array is used where a row has to hold its original
 *                condition AND the profile-step predicate without either
 *                disappearing into a compound name.
 *     to       - destination step, or an EXIT_* terminal outcome
 *
 * A row may also carry `dead: true` when it encodes a branch the guards can
 * never reach in practice but which the provider still contains (see
 * AUTH_ORDERING below).
 *
 * ORDER IS SEMANTIC. Reordering rows within a (from, outcome) group changes
 * behaviour. Two groups below deliberately test the same two predicates in
 * opposite orders -- see AUTH_ORDERING.
 *
 * The pre-extraction routing behaviour this table reproduces is recorded in
 * src/__tests__/fixtures/signUpRouting.baseline.json (rows) and its Markdown
 * companion (provenance and notes), and the unit suite checks every row
 * against it.
 */

import { contextGuards } from './guards'

export const STEPS = {
  authenticate: 'authenticate',
  existingUser: 'existingUser',
  resetPassword: 'resetPassword',
  upsell: 'upsell',
  newUser: 'newUser',
  selectPlan: 'selectPlan',
  billing: 'billing',
}

/** Pseudo-states: routing that happens outside any step's own handler. */
export const PSEUDO_STATES = {
  bootstrap: 'bootstrap',
  global: 'global',
}

/**
 * Terminal outcomes. These leave the step machine; they are not steps. They
 * were the easiest thing to lose in the extraction -- two of them are
 * invisible to a `navigateToStep|window.location` grep -- and modelling them
 * as data does not by itself prevent that: what catches an omitted terminal
 * is the unit suite's coverage check against the baseline fixture, which
 * fails on any fixture row the table neither models nor names.
 *
 * BOUNDARY. The table models every decision made over the provider's own
 * state (user, plan, validation, jwt, order, and the two derived flags). It
 * does not model handleOrderResponse's classification of the order response:
 * that is a function over a wire response, not over machine context, and it
 * has its own home and its own suite. Effects that follow a transition (the
 * delayed redirect after existingUser) are the caller's, not rows.
 */
export const EXITS = {
  authRedirect: 'EXIT_AUTH_REDIRECT',
  ssoUrl: 'EXIT_SSO_URL',
  webappGoogleLogin: 'EXIT_WEBAPP_GOOGLE_LOGIN',
  successPage: 'EXIT_SUCCESS_PAGE',
  appAuthReturn: 'EXIT_APP_AUTH_RETURN',
  appUrl: 'EXIT_APP_URL',
  errorAlert: 'EXIT_ERROR_ALERT',
  failedAuth: 'EXIT_FAILED_AUTH',
  noop: 'EXIT_NOOP',
}

export const OUTCOMES = {
  existingSession: 'EXISTING_SESSION',
  back: 'BACK',
  newSession: 'NEW_SESSION',
  checkoutAbandoned: 'CHECKOUT_ABANDONED',
  resetRequested: 'RESET_REQUESTED',
  googleLinkedExisting: 'GOOGLE_LINKED_EXISTING',
  googleUnlinkedExisting: 'GOOGLE_UNLINKED_EXISTING',
  googleNewPurchasable: 'GOOGLE_NEW_PURCHASABLE',
  googleNewBlocked: 'GOOGLE_NEW_BLOCKED',
  emailExisting: 'EMAIL_EXISTING',
  emailNewPurchasable: 'EMAIL_NEW_PURCHASABLE',
  emailNewBlocked: 'EMAIL_NEW_BLOCKED',
  submitted: 'SUBMITTED',
  authenticatedPurchasable: 'AUTHENTICATED_PURCHASABLE',
  authenticatedNotPurchasable: 'AUTHENTICATED_NOT_PURCHASABLE',
  authenticatedExisting: 'AUTHENTICATED_EXISTING',
  authenticatedUnresolved: 'AUTHENTICATED_UNRESOLVED',
  authFailed: 'AUTH_FAILED',
  googleNotPurchasable: 'GOOGLE_NOT_PURCHASABLE',
  noAuthPath: 'NO_AUTH_PATH',
  noEmail: 'NO_EMAIL',
  orderCompleted: 'ORDER_COMPLETED',
  redirectToApp: 'REDIRECT_TO_APP',
  resetFailed: 'RESET_FAILED',
  validationError: 'VALIDATION_ERROR',
}

/**
 * AUTH_ORDERING -- the asymmetry this table must NOT tidy away.
 *
 * saveStepAuthenticate  tests shouldShowUpsell     FIRST, then shouldSkipPlanSelect.
 * saveStepResetPassword tests shouldSkipPlanSelect FIRST, then shouldShowUpsell.
 *
 * NEAT_HOME_TRIAL_SKU is 'neathomeyrt' and shouldSkipPlanSelect matches
 * `sku.startsWith('neathome')`, so shouldShowUpsell being true IMPLIES
 * shouldSkipPlanSelect is true. In saveStepResetPassword the skip arm therefore
 * always wins and its upsell arm is DEAD CODE.
 *
 * It is preserved as dead on purpose. Collapsing these two groups into one
 * shared "post-authentication" rule changes behaviour on one path or the other
 * whichever order the shared rule picks. Whether the dead branch is the bug is
 * a product question, tracked outside this change; it is not answered here.
 */

export const transitions = [
  // --- bootstrap: the `exists && oauthToken` OAuth-return effect in the plan-fetch useEffect ---
  { from: PSEUDO_STATES.bootstrap, outcome: OUTCOMES.existingSession, guard: 'always', to: STEPS.existingUser },
  // Everyone on this path returned from AUTH_URL with an oauthToken, so
  // squidward has already identified them and answered with a neatUserId: they
  // have an account and must not be sent to create one. See requiresProfileStep
  // and the bootstrap user override in SignUpProvider.js.
  { from: PSEUDO_STATES.bootstrap, outcome: OUTCOMES.newSession, guard: ['skipsBootstrapPlanSelect', 'requiresProfileStep'], to: STEPS.newUser },
  { from: PSEUDO_STATES.bootstrap, outcome: OUTCOMES.newSession, guard: ['skipsBootstrapPlanSelect', 'skipsProfileStep'], to: STEPS.billing },
  { from: PSEUDO_STATES.bootstrap, outcome: OUTCOMES.newSession, guard: 'always', to: STEPS.selectPlan },

  // --- global: leaving /t resets the machine (clearCheckout) ---
  // One of the two paths back to `authenticate`, and the only one that bypasses
  // navigateToStep -- clearCheckout writes `currentStep` itself as part of a
  // whole-state reset. The other is the newUser BACK row below, which is an
  // ordinary transition and does not reset anything.
  { from: PSEUDO_STATES.global, outcome: OUTCOMES.checkoutAbandoned, guard: 'always', to: STEPS.authenticate },

  // --- global: the reset-password request (handleForgotPassword) ---
  // Both failure arms (a non-2xx response and the surrounding catch) alert the
  // same message, so they are one outcome.
  { from: PSEUDO_STATES.global, outcome: OUTCOMES.resetRequested, guard: 'always', to: STEPS.resetPassword },
  { from: PSEUDO_STATES.global, outcome: OUTCOMES.resetFailed, guard: 'always', to: EXITS.errorAlert },

  // --- global: handleRedirectToApp -- jwt first, then the order's ssoUrl, else the app root ---
  { from: PSEUDO_STATES.global, outcome: OUTCOMES.redirectToApp, guard: 'hasJwt', to: EXITS.appAuthReturn },
  { from: PSEUDO_STATES.global, outcome: OUTCOMES.redirectToApp, guard: 'orderHasSsoUrl', to: EXITS.ssoUrl },
  { from: PSEUDO_STATES.global, outcome: OUTCOMES.redirectToApp, guard: 'always', to: EXITS.appUrl },

  // --- authenticate (saveStepAuthenticate) ---
  // A validate response carrying an error ends the attempt before any lead is
  // created or any of the branches below are reached.
  { from: STEPS.authenticate, outcome: OUTCOMES.validationError, guard: 'always', to: EXITS.errorAlert },
  // Google user with an existing, linked, purchasable account.
  { from: STEPS.authenticate, outcome: OUTCOMES.googleLinkedExisting, guard: 'shouldShowUpsell', to: STEPS.upsell },
  { from: STEPS.authenticate, outcome: OUTCOMES.googleLinkedExisting, guard: 'always', to: STEPS.billing },
  // Note: this group never consults shouldSkipPlanSelect, so a linked Google
  // user with an existing account can reach neither selectPlan nor newUser
  // from here. Asymmetric with the two groups below; preserved.
  { from: STEPS.authenticate, outcome: OUTCOMES.googleUnlinkedExisting, guard: 'always', to: EXITS.authRedirect },

  // Google user, no existing account. UPSELL IS TESTED FIRST -- see AUTH_ORDERING.
  { from: STEPS.authenticate, outcome: OUTCOMES.googleNewPurchasable, guard: 'shouldShowUpsell', to: STEPS.upsell },
  { from: STEPS.authenticate, outcome: OUTCOMES.googleNewPurchasable, guard: ['shouldSkipPlanSelect', 'requiresProfileStep'], to: STEPS.newUser },
  // A Google user who also skips plan select goes straight to billing. The
  // row above cannot fire for this outcome -- a googleNewPurchasable context
  // always carries a googleId -- but it stays named so a half-deletion of the
  // predicate fails the structural check over newUser rows.
  { from: STEPS.authenticate, outcome: OUTCOMES.googleNewPurchasable, guard: ['shouldSkipPlanSelect', 'skipsProfileStep'], to: STEPS.billing },
  { from: STEPS.authenticate, outcome: OUTCOMES.googleNewPurchasable, guard: 'always', to: STEPS.selectPlan },
  { from: STEPS.authenticate, outcome: OUTCOMES.googleNewBlocked, guard: 'always', to: EXITS.errorAlert },

  // Email user with an existing account.
  { from: STEPS.authenticate, outcome: OUTCOMES.emailExisting, guard: 'always', to: EXITS.authRedirect },

  // Email user, no existing account. UPSELL IS TESTED FIRST -- see AUTH_ORDERING.
  { from: STEPS.authenticate, outcome: OUTCOMES.emailNewPurchasable, guard: 'shouldShowUpsell', to: STEPS.upsell },
  // Behaviourally unchanged: an emailNewPurchasable context has neither a
  // googleId nor a neatUserId, so requiresProfileStep is always true here. The
  // guard is named so the structural check over newUser rows holds, and no
  // bypass row is added because it could never fire.
  { from: STEPS.authenticate, outcome: OUTCOMES.emailNewPurchasable, guard: ['shouldSkipPlanSelect', 'requiresProfileStep'], to: STEPS.newUser },
  { from: STEPS.authenticate, outcome: OUTCOMES.emailNewPurchasable, guard: 'always', to: STEPS.selectPlan },
  { from: STEPS.authenticate, outcome: OUTCOMES.emailNewBlocked, guard: 'always', to: EXITS.errorAlert },

  // --- selectPlan (saveStepSelectPlan) ---
  { from: STEPS.selectPlan, outcome: OUTCOMES.submitted, guard: 'hasNeatUserId', to: STEPS.billing },
  { from: STEPS.selectPlan, outcome: OUTCOMES.submitted, guard: 'requiresProfileStep', to: STEPS.newUser },
  // A Google user with no neatUserId: the row above already covers the
  // neatUserId half, so this catches the remaining bypass arm.
  { from: STEPS.selectPlan, outcome: OUTCOMES.submitted, guard: 'always', to: STEPS.billing },

  // --- existingUser (saveStepExistingUser) ---
  // This group never consults shouldShowUpsell: `upsell` is unreachable from
  // `existingUser`. Asymmetric with resetPassword below; preserved.
  { from: STEPS.existingUser, outcome: OUTCOMES.authenticatedPurchasable, guard: 'shouldSkipPlanSelect', to: STEPS.billing },
  { from: STEPS.existingUser, outcome: OUTCOMES.authenticatedPurchasable, guard: 'always', to: STEPS.selectPlan },
  { from: STEPS.existingUser, outcome: OUTCOMES.authenticatedNotPurchasable, guard: 'always', to: EXITS.ssoUrl },
  { from: STEPS.existingUser, outcome: OUTCOMES.authFailed, guard: 'always', to: EXITS.failedAuth },
  { from: STEPS.existingUser, outcome: OUTCOMES.googleNotPurchasable, guard: 'always', to: EXITS.webappGoogleLogin },
  { from: STEPS.existingUser, outcome: OUTCOMES.noAuthPath, guard: 'always', to: EXITS.errorAlert },

  // --- resetPassword (saveStepResetPassword) ---
  // SKIP-PLAN-SELECT IS TESTED FIRST here -- the opposite of `authenticate`.
  // The upsell row below is DEAD CODE and is preserved as such. See AUTH_ORDERING.
  { from: STEPS.resetPassword, outcome: OUTCOMES.noEmail, guard: 'always', to: EXITS.errorAlert },
  { from: STEPS.resetPassword, outcome: OUTCOMES.authenticatedPurchasable, guard: 'shouldSkipPlanSelect', to: STEPS.billing },
  { from: STEPS.resetPassword, outcome: OUTCOMES.authenticatedPurchasable, guard: 'shouldShowUpsell', to: STEPS.upsell, dead: true },
  { from: STEPS.resetPassword, outcome: OUTCOMES.authenticatedPurchasable, guard: 'always', to: STEPS.selectPlan },
  { from: STEPS.resetPassword, outcome: OUTCOMES.authenticatedExisting, guard: 'always', to: EXITS.ssoUrl },
  // saveStepResetPassword's authenticated if/else-if chain has no final else:
  // this state is a silent no-op today. Modelled explicitly so it is visible
  // rather than implicit.
  { from: STEPS.resetPassword, outcome: OUTCOMES.authenticatedUnresolved, guard: 'always', to: EXITS.noop },
  { from: STEPS.resetPassword, outcome: OUTCOMES.authFailed, guard: 'always', to: EXITS.errorAlert },

  // --- upsell (saveStepUpsell) --- unconditional; acceptOffer only swaps
  // plan/coupon, it does not fork the destination.
  { from: STEPS.upsell, outcome: OUTCOMES.submitted, guard: 'requiresProfileStep', to: STEPS.newUser },
  // Reached by both Google arms that can land on upsell: googleNewPurchasable
  // (no account yet) and googleLinkedExisting (has one). Neither needs to
  // create a profile.
  { from: STEPS.upsell, outcome: OUTCOMES.submitted, guard: 'always', to: STEPS.billing },

  // --- newUser (saveStepNewUser) --- unconditional.
  { from: STEPS.newUser, outcome: OUTCOMES.submitted, guard: 'always', to: STEPS.billing },

  // --- newUser: the back edge ---
  // The ONLY back edge in the table. The merged frame puts the email step and
  // the profile step in one visual surface, so returning to the email step is a
  // transition rather than a navigation; plan selection, upsell and billing get
  // no equivalent row, and a grep for OUTCOMES.back over this file is what
  // proves it.
  { from: STEPS.newUser, outcome: OUTCOMES.back, guard: 'always', to: STEPS.authenticate },

  // --- global: a completed order leaves the machine (handleOrderResponse) ---
  // A Next router push, not a step change or a window.location write.
  { from: PSEUDO_STATES.global, outcome: OUTCOMES.orderCompleted, guard: 'always', to: EXITS.successPage },
]

/** True when `to` is a terminal outcome rather than a step. */
export const isExit = (to) => Object.values(EXITS).includes(to)

/**
 * Resolve a destination. Rows are scanned in declaration order and the first
 * matching guard wins, which is exactly how the if/else-if chains behaved.
 *
 * Returns null when no row matches, rather than guessing -- an unmodelled
 * (from, outcome) pair is a bug in the table and should surface as one.
 */
export const guardNamesOf = (row) => [].concat(row.guard)

export const resolveTransition = (from, outcome, context = {}) => {
  const row = transitions.find((t) => (
    t.from === from && t.outcome === outcome
    && guardNamesOf(t).every((name) => contextGuards[name](context))
  ))
  return row ? row.to : null
}
